import { WidgetId } from 'widgets/WidgetId';
import React, { useContext, useRef } from 'react';
import classNames from 'classnames';
import {
    TileDragItem,
    useTileDrag,
    useTileDrop,
} from 'widgets/tiles/components/dragAndDrop/hooks';
import './Tile.scss';
import AppContext from 'app/AppContext';
import Widget from 'widgets/Widget';
import { TileContextProvider } from 'widgets/tiles/components/tile/TileContext';

export interface TileProps {
    index: number;
    tilesWidgetKey: string;
    widgetId: WidgetId;
    moveTile: (
        draggedTileDragItem: TileDragItem,
        targetTileDragItem: TileDragItem
    ) => void;
    removeTile: () => void;
    className?: string;
    disableSymbolConfig: boolean;
}

/*
 * This is a wrapper for a Tile Widget that appears within a CapsulesContainer (Capsule Container)
 */
export function Tile(props: TileProps) {
    const {
        className,
        tilesWidgetKey,
        widgetId,
        moveTile,
        removeTile,
        disableSymbolConfig,
    } = props;

    const { widgetKey, widgetType } = widgetId;

    const { services } = useContext(AppContext);
    const { widgetsService } = services;

    const tileDragDropItem: TileDragItem = {
        tilesWidgetKey,
        tileWidgetId: widgetId,
    };

    const ref = useRef<HTMLDivElement>(null);
    const { canDrop, drop } = useTileDrop(tileDragDropItem, ref, moveTile);
    drop(ref);

    const contentRef = useRef<HTMLDivElement>(null);
    const { isDragging, drag } = useTileDrag(tileDragDropItem, removeTile);
    drag(contentRef);

    const cssClasses = classNames(
        'tile-container',
        isDragging && 'is-dragging',
        canDrop && 'is-dropping',
        className
    );

    const WidgetComponent = widgetsService.getReactComponentType(widgetType);

    return (
        <div ref={ref} className={cssClasses}>
            <div ref={contentRef} className="tile">
                <TileContextProvider
                    tilesWidgetKey={tilesWidgetKey}
                    disableSymbolConfig={disableSymbolConfig}
                >
                    <Widget
                        component={WidgetComponent}
                        host={'tile'}
                        widgetKey={widgetKey}
                        widgetType={widgetType}
                        width={0}
                        height={0}
                    />
                </TileContextProvider>
            </div>
        </div>
    );
}
